/**
 * Statement Duplicate Census diagnostic card (read-only).
 *
 * CLAUDE.md task: prod shows duplicate Management Statement rows for the
 * same property/month, but the Statement Duplicate Cleanup card above
 * reports "No duplicate statements found" -- its grouping key (same
 * source email + resolved property + month) apparently doesn't match the
 * shape of prod's actual duplicates. This card groups the same
 * non-excluded doc set under four independent candidate identity keys
 * (statement_id, property_ref+month, normalized name+month, and a
 * last-resort entity+amounts+month content key) and reports duplicate-
 * group counts under each, plus a side-by-side dump of up to 10 groups
 * under the loosest key that actually found any -- so the real shape is
 * visible from the numbers, not guessed. GET only, no Apply, no writes.
 */

import { useState } from 'react';
import { Loader2, Search, AlertTriangle, Info, Unlink, CheckCircle2, ShieldCheck, Trash2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { statementDuplicateCensusAPI, statementIdentityMigrationAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatDate(isoStr) {
  if (!isoStr) return '--';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

function KeySummaryTable({ keys, exampleKey }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-left">
            <th className="py-1.5 pr-3 font-medium">Key</th>
            <th className="py-1.5 pr-3 font-medium">Definition</th>
            <th className="py-1.5 pr-3 font-medium">Eligible docs</th>
            <th className="py-1.5 pr-3 font-medium">Duplicate groups</th>
            <th className="py-1.5 pr-3 font-medium">Duplicate docs</th>
          </tr>
        </thead>
        <tbody>
          {keys.map(function (k) {
            var isExample = k.key === exampleKey;
            return (
              <tr key={k.key} className={'border-b border-border/50' + (isExample ? ' bg-amber-50 dark:bg-amber-950/20' : '')}>
                <td className="py-1.5 pr-3 font-mono text-xs">
                  {k.key}
                  {isExample && (
                    <Badge className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]">
                      shown below
                    </Badge>
                  )}
                </td>
                <td className="py-1.5 pr-3 text-xs text-muted-foreground">{k.description}</td>
                <td className="py-1.5 pr-3 text-xs">{k.eligible_docs_count}</td>
                <td className="py-1.5 pr-3 text-xs">
                  {k.duplicate_groups_count > 0 ? (
                    <span className="text-amber-700 dark:text-amber-400 font-medium">{k.duplicate_groups_count}</span>
                  ) : k.duplicate_groups_count}
                </td>
                <td className="py-1.5 pr-3 text-xs">{k.duplicate_docs_count}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DocRow({ doc }) {
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 pr-3 font-mono text-xs">{doc.statement_id || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.property_ref || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.property_name_raw || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.statement_month || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.statement_date || '--'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.source}{doc.source_email_id ? ' / ' + doc.source_email_id : ''}</td>
      <td className="py-1.5 pr-3 text-xs text-muted-foreground">{formatDate(doc.created_at)}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.email_statement_count_present ? 'yes' : 'no'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.has_matches ? 'yes' : 'no'}</td>
      <td className="py-1.5 pr-3 text-xs">{doc.excluded_as_duplicate ? 'yes' : 'no'}</td>
    </tr>
  );
}

function ExampleGroup({ group }) {
  return (
    <div className="border border-border rounded p-3">
      <p className="text-xs text-muted-foreground mb-2 font-mono">{group.group_key}</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-1.5 pr-3 font-medium">Statement ID</th>
              <th className="py-1.5 pr-3 font-medium">Property ref</th>
              <th className="py-1.5 pr-3 font-medium">Property name (raw)</th>
              <th className="py-1.5 pr-3 font-medium">Month</th>
              <th className="py-1.5 pr-3 font-medium">Statement date</th>
              <th className="py-1.5 pr-3 font-medium">Source / ingest id</th>
              <th className="py-1.5 pr-3 font-medium">Created</th>
              <th className="py-1.5 pr-3 font-medium">email_statement_count</th>
              <th className="py-1.5 pr-3 font-medium">Has matches</th>
              <th className="py-1.5 pr-3 font-medium">Excluded</th>
            </tr>
          </thead>
          <tbody>
            {group.docs.map(function (doc) {
              return <DocRow key={doc.id} doc={doc} />;
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OrphansByStatusTable({ byStatus }) {
  var keys = Object.keys(byStatus);
  if (keys.length === 0) {
    return <p className="text-xs text-muted-foreground">No orphaned links found.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-left">
          <th className="py-1.5 pr-3 font-medium">Match status</th>
          <th className="py-1.5 pr-3 font-medium">Count</th>
        </tr>
      </thead>
      <tbody>
        {keys.map(function (status) {
          return (
            <tr key={status} className="border-b border-border/50">
              <td className="py-1.5 pr-3 font-mono text-xs">{status}</td>
              <td className="py-1.5 pr-3 text-xs">{byStatus[status]}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OrphanRow({ orphan }) {
  var twin = orphan.surviving_twin;
  var rpm = orphan.recovered_property_month;
  return (
    <tr className="border-b border-border/50">
      <td className="py-1.5 pr-3 font-mono text-xs">{orphan.match_id}</td>
      <td className="py-1.5 pr-3 font-mono text-xs">{orphan.statement_id_referenced}</td>
      <td className="py-1.5 pr-3 text-xs">{orphan.status}</td>
      <td className="py-1.5 pr-3 font-mono text-xs">{orphan.transaction_id}</td>
      <td className="py-1.5 pr-3 text-xs">
        {rpm.landlord_entity_id || '--'} / {rpm.approx_month_from_txn_date || '--'}
      </td>
      <td className="py-1.5 pr-3 text-xs">{orphan.confirm_provenance.confirmed_via || 'manual'}</td>
      <td className="py-1.5 pr-3 text-xs">
        {twin.re_pointable ? (
          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]">
            re-pointable: {twin.candidate_statement_ids.join(', ')}
          </Badge>
        ) : (
          <span className="text-muted-foreground">no surviving twin</span>
        )}
      </td>
    </tr>
  );
}

export function StatementDuplicateCensusPanel() {
  var [data, setData] = useState(null);
  var [loading, setLoading] = useState(false);
  var [orphanData, setOrphanData] = useState(null);
  var [orphanLoading, setOrphanLoading] = useState(false);
  var [migrationData, setMigrationData] = useState(null);
  var [migrationLoading, setMigrationLoading] = useState(false);
  var [migrationRunning, setMigrationRunning] = useState(false);
  var [migrationResult, setMigrationResult] = useState(null);
  var [migrationError, setMigrationError] = useState(null);

  var runCensus = async function () {
    setLoading(true);
    try {
      var res = await statementDuplicateCensusAPI.run();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var runOrphanCheck = async function () {
    setOrphanLoading(true);
    try {
      var res = await statementDuplicateCensusAPI.runOrphanedLinks();
      setOrphanData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setOrphanLoading(false);
    }
  };

  var checkMigrationReadiness = async function () {
    setMigrationLoading(true);
    setMigrationResult(null);
    setMigrationError(null);
    try {
      var res = await statementIdentityMigrationAPI.preflight();
      setMigrationData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setMigrationLoading(false);
    }
  };

  var runMigration = async function () {
    setMigrationRunning(true);
    setMigrationError(null);
    try {
      var res = await statementIdentityMigrationAPI.apply();
      setMigrationResult(res.data);
    } catch (err) {
      setMigrationError(getErrorMessage(err));
      toast.error(getErrorMessage(err));
    } finally {
      setMigrationRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Search className="h-4 w-4" />
          Statement Duplicate Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Read-only. Groups every non-excluded management statement under
          four independent candidate identity keys (statement_id;
          property_ref+month; normalized property name+month; and a
          last-resort entity+amounts+month content key) to show which key
          actually matches prod's duplicate rows, since the Statement
          Duplicate Cleanup card's own key can report zero groups while
          duplicates still exist. Never writes.
        </p>

        <Button onClick={runCensus} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Run Census
        </Button>

        {data && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <Info className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                {data.total_docs_scanned} document{data.total_docs_scanned !== 1 ? 's' : ''} scanned at {formatDate(data.checked_at)}
              </span>
            </div>

            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                Current cleanup card groups on: <span className="font-mono">{data.current_service_group_key.description}</span>
              </p>
              <p>
                Code reference: <span className="font-mono">{data.current_service_group_key.code_reference}</span>
              </p>
              <p>
                Missing property_ref: <span className="font-medium text-foreground">{data.null_field_counts.missing_property_ref}</span>
                {'  ·  '}
                Missing statement_id: <span className="font-medium text-foreground">{data.null_field_counts.missing_statement_id}</span>
              </p>
            </div>

            <KeySummaryTable keys={data.keys} exampleKey={data.example_key} />

            {data.example_key ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <span>
                    Showing up to {data.example_groups.length} duplicate group{data.example_groups.length !== 1 ? 's' : ''} under the loosest matching key: <span className="font-mono">{data.example_key}</span>
                  </span>
                </div>
                {data.example_groups.map(function (group) {
                  return <ExampleGroup key={group.group_key} group={group} />;
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">No duplicate groups found under any candidate key.</p>
            )}
          </div>
        )}

        <div className="border-t pt-4 space-y-3">
          <div>
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Unlink className="h-4 w-4" />
              Orphaned Links
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              The Management Statements trash icon hard-deletes the statement
              doc with no cleanup of anything that references it. Finds every
              statement_payout_matches doc still pointing at a dead
              statement_id and flags which ones have a surviving
              same-(landlord entity, month) twin they could be re-pointed to.
              This only identifies the restore-candidate population -- it
              never writes or re-points anything itself.
            </p>
          </div>

          <Button onClick={runOrphanCheck} disabled={orphanLoading} variant="outline">
            {orphanLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Unlink className="h-4 w-4 mr-2" />}
            Check Orphaned Links
          </Button>

          {orphanData && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 text-xs border border-border rounded p-2">
                <CheckCircle2 className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <div>
                  <p>
                    <span className="font-medium">Trash icon behavior:</span>{' '}
                    {orphanData.trash_delete_behavior.is_hard_delete ? 'hard delete' : 'soft exclude'}
                  </p>
                  <p className="text-muted-foreground mt-0.5">{orphanData.trash_delete_behavior.note}</p>
                  <p className="font-mono text-muted-foreground mt-0.5">{orphanData.trash_delete_behavior.backend_code_reference}</p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">{orphanData.booking_match_statuses_note}</p>

              <div className="flex items-center gap-2 text-sm">
                <Info className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">
                  {orphanData.matches_scanned} of {orphanData.matches_total} statement_payout_matches scanned at {formatDate(orphanData.checked_at)}
                  {orphanData.truncated && (
                    <Badge className="ml-2 bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]">
                      truncated
                    </Badge>
                  )}
                </span>
              </div>

              <p className="text-xs">
                Orphans found: <span className="font-medium text-foreground">{orphanData.orphan_count}</span>
                {'  ·  '}
                Re-pointable: <span className="font-medium text-foreground">{orphanData.re_pointable_count}</span>
              </p>

              <OrphansByStatusTable byStatus={orphanData.orphans_by_status} />

              {orphanData.orphans.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1.5 pr-3 font-medium">Match ID</th>
                        <th className="py-1.5 pr-3 font-medium">Statement ID referenced</th>
                        <th className="py-1.5 pr-3 font-medium">Status</th>
                        <th className="py-1.5 pr-3 font-medium">Transaction</th>
                        <th className="py-1.5 pr-3 font-medium">Entity / approx month</th>
                        <th className="py-1.5 pr-3 font-medium">Confirmed via</th>
                        <th className="py-1.5 pr-3 font-medium">Surviving twin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphanData.orphans.map(function (orphan) {
                        return <OrphanRow key={orphan.match_id} orphan={orphan} />;
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t pt-4 space-y-3">
          <div>
            <h4 className="text-sm font-medium flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Legacy Index Migration
            </h4>
            <p className="text-xs text-muted-foreground mt-1">
              The ingest write identity moved from statement_id to (property_ref,
              else normalized property name) + statement_month, enforced by two
              new partial-unique indexes created at every startup. The old
              uniq_mgmt_stmt_statement_id unique index is only dropped here,
              and only once a LIVE re-check confirms the new indexes are live
              and zero duplicate groups remain under the new key -- the same
              gate the Statement Duplicate Cleanup card above resolves.
              Idempotent: running it again once already migrated reports that,
              not an error.
            </p>
          </div>

          <div className="flex gap-2">
            <Button onClick={checkMigrationReadiness} disabled={migrationLoading} variant="outline">
              {migrationLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
              Check Migration Readiness
            </Button>

            <Button onClick={runMigration} disabled={migrationRunning} variant="destructive">
              {migrationRunning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Drop Legacy Index
            </Button>
          </div>

          {migrationData && (
            <div className="text-xs space-y-1 border border-border rounded p-2">
              <p>
                New indexes live: <span className="font-medium text-foreground">{migrationData.new_indexes_live ? 'yes' : 'no'}</span>
                {'  ·  '}
                Already migrated: <span className="font-medium text-foreground">{migrationData.already_applied ? 'yes' : 'no'}</span>
              </p>
              <p>
                Duplicate groups under new key: <span className={'font-medium ' + (migrationData.duplicate_groups_count > 0 ? 'text-amber-700 dark:text-amber-400' : 'text-foreground')}>{migrationData.duplicate_groups_count}</span>
                {migrationData.duplicate_groups_count > 0 && (
                  <span className="text-muted-foreground"> ({migrationData.duplicate_statement_ids.join(', ')})</span>
                )}
              </p>
              <p>
                Can apply now: <span className="font-medium text-foreground">{migrationData.can_apply ? 'yes' : 'no'}</span>
              </p>

              {migrationData.last_startup_index_creation ? (
                <div className="mt-1 pt-1 border-t border-border/50">
                  <p className="text-muted-foreground">
                    Last startup index creation ({formatDate(migrationData.last_startup_index_creation.timestamp)}):
                  </p>
                  <ul className="mt-0.5 space-y-0.5">
                    {migrationData.last_startup_index_creation.results.map(function (r) {
                      return (
                        <li key={r.index} className="font-mono">
                          {r.index}:{' '}
                          {r.status === 'failed' ? (
                            <span className="text-red-600 dark:text-red-400 font-medium">FAILED -- {r.error}</span>
                          ) : (
                            <span className={r.status === 'created' ? 'text-emerald-700 dark:text-emerald-400' : ''}>
                              {r.status}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <p className="mt-1 pt-1 border-t border-border/50 text-muted-foreground">
                  Last startup index creation: no record yet (backend has not restarted since this was added).
                </p>
              )}
            </div>
          )}

          {migrationError && (
            <div className="flex items-start gap-2 text-xs border border-red-300 dark:border-red-800 rounded p-2 bg-red-50 dark:bg-red-950/20">
              <XCircle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <p>{migrationError}</p>
            </div>
          )}

          {migrationResult && (
            <div className="flex items-start gap-2 text-xs border border-emerald-300 dark:border-emerald-800 rounded p-2 bg-emerald-50 dark:bg-emerald-950/20">
              <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
              <div>
                {migrationResult.status === 'already_applied' ? (
                  <p>Already migrated. Legacy index <span className="font-mono">{migrationResult.old_index_name}</span> was already dropped.</p>
                ) : (
                  <p>
                    Dropped index <span className="font-mono">{migrationResult.old_index_name}</span>
                    {migrationResult.index_drop_performed === false && ' (already absent)'}.
                  </p>
                )}
                <p className="text-muted-foreground mt-0.5">
                  Remaining partial-unique indexes: <span className="font-mono">{(migrationResult.new_index_names || []).join(', ')}</span>
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
